import { create } from "zustand";
import { fetchAdminCustomers, toAdminClientSummaryFromDetail, updateAdminCustomerStatus } from "@/lib/adminCustomersApi";
import { fetchModuleFlags } from "@/lib/bootstrapApi";
import { fetchPriceTables } from "@/lib/adminPriceTablesApi";
import { fetchCurrentAccount, logoutAccount, toAccount } from "@/lib/authApi";
import { clearCart as clearCartApi, fetchCart, replaceCart } from "@/lib/cartApi";
import { fetchCatalogSnapshot } from "@/lib/catalogApi";
import { fetchMyDeliveryUnits, type CustomerDeliveryUnit } from "@/lib/deliveryUnitsApi";
import { roundQuantity } from "@/lib/number";
import {
  checkoutOrder,
  deleteMyOrder,
  updateMyOrder as updateMyOrderApi,
  updateOrderStatus as updateOrderStatusApi,
} from "@/lib/ordersApi";
import {
  isAdminEquivalent,
  type Account,
  type AdminClientSummary,
  type CartItem,
  type CatalogCategory,
  type CatalogCutType,
  type CatalogUnit,
  type ClientType,
  type CommercialStatus,
  type CompanyProfile,
  type Order,
  type OrderStatus,
  type PriceTable,
  type Product,
} from "@/lib/types";

interface PlaceOrderInput {
  deliveryWindow?: string;
  notes?: string;
  deliveryUnitId?: string;
  priceChangeAck?: boolean;
}

type DomainState = {
  cart: CartItem[];
  clients: AdminClientSummary[];
  priceTables: PriceTable[];
  catalogCategories: CatalogCategory[];
  catalogCutTypes: CatalogCutType[];
  catalogUnits: CatalogUnit[];
  products: Product[];
  orders: Order[];
  catalogStatus: "idle" | "loading" | "ready" | "error";
  catalogError: string | null;
  // Unidade de compra escolhida ANTES/durante o pedido: dirige o preço da vitrine, do carrinho e do
  // checkout, para o cliente ver o mesmo preço do começo ao fim. Estado de sessão (resetado no logout).
  deliveryUnits: CustomerDeliveryUnit[];
  deliveryUnitsStatus: "idle" | "loading" | "ready";
  selectedDeliveryUnitId: string | null;
  clientsStatus: "idle" | "loading" | "ready" | "error";
  clientsError: string | null;
  priceTablesStatus: "idle" | "loading" | "ready" | "error";
  priceTablesError: string | null;
};

type SessionState = Pick<DomainState, "cart" | "orders">;

const buildInitialState = (): DomainState => ({
  cart: [],
  clients: [],
  priceTables: [],
  catalogCategories: [],
  catalogCutTypes: [],
  catalogUnits: [],
  products: [],
  orders: [],
  catalogStatus: "idle",
  catalogError: null,
  deliveryUnits: [],
  deliveryUnitsStatus: "idle",
  selectedDeliveryUnitId: null,
  clientsStatus: "idle",
  clientsError: null,
  priceTablesStatus: "idle",
  priceTablesError: null,
});

const emptySessionState = (): SessionState => ({
  cart: [],
  orders: [],
});

const buildLoggedOutState = () => ({
  account: null as Account | null,
  authStatus: "ready" as const,
  ...buildInitialState(),
});

function mergeOrder(orders: Order[], updated: Order) {
  return orders.map((order) => (order.id === updated.id ? updated : order));
}

async function persistCartForAccount(cart: CartItem[]) {
  const state = useApp.getState();
  const account = state.account;
  if (!account || account.role !== "CLIENT") return cart;
  const saved = await replaceCart(cart);
  // O PUT reprecifica na tabela do cliente; se há unidade selecionada, reprecifica pela unidade para o
  // carrinho refletir o mesmo preço da vitrine/checkout. Sem unidade, mantém o resultado do PUT.
  const unitId = state.selectedDeliveryUnitId;
  return unitId ? fetchCart(unitId) : saved;
}

interface AppState extends DomainState {
  account: Account | null;
  authStatus: "loading" | "ready";
  /**
   * Flag de módulo "NF de fornecedor" vinda do bootstrap do backend (fonte de verdade). Default `false`
   * = módulo escondido até o backend afirmar que está ligado (fail-safe). Não é estado de sessão: não é
   * resetado no logout.
   */
  supplierNfEnabled: boolean;
  /**
   * Estado da consulta das flags de módulo (bootstrap): distingue CARREGANDO (ainda não se sabe) de
   * PRONTO (resposta recebida) e FALHA (consulta errou). A rota de NF só decide o redirect quando PRONTO —
   * em CARREGANDO espera; em FALHA bloqueia com opção de retry. Evita o redirect indevido em reload/URL direta.
   */
  moduleFlagsStatus: "loading" | "ready" | "error";
  loadModuleFlags: () => Promise<void>;
  cartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  setAccount: (account: Account | null) => void;
  bootstrapAuth: () => Promise<void>;
  /**
   * Revalida identidade/permissões via /auth/me SEM mexer em cart/orders nem no authStatus (refresh
   * silencioso). Usado ao navegar, ao focar a janela e ao receber 403, para que uma sessão já aberta
   * reflita mudanças de acesso sem recarregar a página. Em falha transitória mantém a sessão atual;
   * a perda real de sessão (401) é tratada em lib/api.ts (logout + /login).
   */
  refreshAccount: () => Promise<void>;
  bootstrapSessionData: () => Promise<void>;
  signup: (account: Account) => { ok: boolean; admin: boolean };
  login: (account: Account) => { ok: boolean; admin: boolean };
  logout: () => Promise<void>;
  completeOnboarding: (profile: CompanyProfile) => void;
  addToCart: (item: Omit<CartItem, "id">) => Promise<void>;
  updateQty: (id: string, qty: number) => Promise<void>;
  removeFromCart: (id: string) => Promise<void>;
  clearCart: () => Promise<void>;
  bootstrapAdminCustomers: () => Promise<void>;
  setClientStatus: (
    clientId: string,
    status: CommercialStatus,
    clientType?: ClientType,
    priceTableId?: string | null,
  ) => Promise<void>;
  bootstrapPriceTables: () => Promise<void>;
  loadDeliveryUnits: () => Promise<void>;
  setSelectedDeliveryUnit: (unitId: string | null) => Promise<void>;
  hydrateCatalog: () => Promise<void>;
  placeOrder: (input: PlaceOrderInput) => Promise<Order | null>;
  updateOrder: (
    orderId: string,
    input: {
      deliveryWindow?: string | null;
      notes?: string | null;
      items?: Array<{
        externalId?: string;
        productId: string;
        unit: string;
        quantity: number;
        cut?: string | null;
      }>;
    },
  ) => Promise<Order>;
  deleteOrder: (orderId: string) => Promise<void>;
  setOrderStatus: (orderId: string, status: OrderStatus) => Promise<void>;
}

export const useApp = create<AppState>()((set, get) => {
  let lastCartMutationId = 0;
  // Sequência da hidratação do catálogo: na carga inicial o efeito do Catalog dispara uma hidratação sem
  // unidade ANTES de o AppLayout auto-selecionar a unidade única, gerando uma segunda hidratação COM
  // unidade. Este contador garante que só a resposta mais recente vença (evita preço account-level obsoleto).
  let lastCatalogHydrationId = 0;

  const setSession = (account: Account | null, session: SessionState = emptySessionState()) => {
    set({
      account,
      authStatus: "ready",
      cart: session.cart,
      orders: session.orders,
    });
  };

  const syncCart = async (
    nextCart: CartItem[],
    persist: () => Promise<CartItem[]>,
  ) => {
    const mutationId = ++lastCartMutationId;
    set({ cart: nextCart });

    try {
      const cart = await persist();
      if (mutationId !== lastCartMutationId) {
        return;
      }
      set({ cart });
    } catch {
      if (mutationId !== lastCartMutationId) {
        return;
      }
      await get().bootstrapSessionData();
    }
  };

  return {
    account: null,
    authStatus: "loading",
    supplierNfEnabled: false,
    moduleFlagsStatus: "loading",
    cartOpen: false,
    ...buildInitialState(),

    loadModuleFlags: async () => {
      set({ moduleFlagsStatus: "loading" });
      try {
        const { supplierNfEnabled } = await fetchModuleFlags();
        set({ supplierNfEnabled, moduleFlagsStatus: "ready" });
      } catch {
        // Falha de consulta: distinta de "desligado". Mantém escondido (fail-safe) e sinaliza erro para o
        // guard bloquear COM opção de retry — nunca "liberar por engano" nem redirecionar como se fosse off.
        set({ supplierNfEnabled: false, moduleFlagsStatus: "error" });
      }
    },
    setCartOpen: (open) => set({ cartOpen: open }),
    setAccount: (account) => setSession(account),
    bootstrapAuth: async () => {
      try {
        const account = toAccount(await fetchCurrentAccount());
        setSession(account);
      } catch {
        set(buildLoggedOutState());
      }
    },
    refreshAccount: async () => {
      const current = get().account;
      if (!current) return; // só com sessão ativa
      try {
        const fresh = toAccount(await fetchCurrentAccount());
        const same =
          fresh.role === current.role &&
          fresh.email === current.email &&
          fresh.status === current.status &&
          [...(fresh.permissions ?? [])].sort().join(",") ===
            [...(current.permissions ?? [])].sort().join(",");
        if (same) return; // evita re-render desnecessário a cada navegação/foco
        set({ account: fresh }); // preserva cart/orders/authStatus
      } catch {
        /* falha transitória: mantém a sessão; 401 é tratado em lib/api.ts */
      }
    },
    bootstrapSessionData: async () => {
      const account = get().account;
      if (!account || account.role !== "CLIENT") return;

      try {
        const cart = await fetchCart(get().selectedDeliveryUnitId ?? undefined);
        setSession(account, { cart, orders: get().orders });
      } catch {
        setSession(account);
      }
    },
    bootstrapAdminCustomers: async () => {
      set({ clientsStatus: "loading", clientsError: null });
      try {
        const customers = await fetchAdminCustomers();
        set({ clients: customers, clientsStatus: "ready", clientsError: null });
      } catch {
        set({ clients: [], clientsStatus: "error", clientsError: "Nao foi possivel carregar os clientes" });
      }
    },
    bootstrapPriceTables: async () => {
      set({ priceTablesStatus: "loading", priceTablesError: null });
      try {
        const priceTables = await fetchPriceTables();
        set({ priceTables, priceTablesStatus: "ready", priceTablesError: null });
      } catch {
        set({
          priceTables: [],
          priceTablesStatus: "error",
          priceTablesError: "Nao foi possivel carregar as tabelas de preco",
        });
      }
    },
    signup: (account) => {
      setSession(account);
      return { ok: true, admin: isAdminEquivalent(account.role) };
    },
    login: (account) => {
      setSession(account);
      return { ok: true, admin: isAdminEquivalent(account.role) };
    },
    logout: async () => {
      // Invalidates the BE session + clears the JSESSIONID cookie. If it errors
      // (e.g. already expired) we still drop the local state so the UI exits.
      try { await logoutAccount(); } catch { /* ignore */ }
      set(buildLoggedOutState());
    },
    completeOnboarding: (profile) =>
      set((state) => ({ account: state.account ? { ...state.account, profile } : null })),
    addToCart: async (item) => {
      const account = get().account;
      if (!account || account.role !== "CLIENT") return;

      const nextCart = [
        ...get().cart,
        { ...item, id: crypto.randomUUID(), quantity: roundQuantity(item.quantity, 3) },
      ];

      await syncCart(nextCart, () => persistCartForAccount(nextCart));
    },
    updateQty: async (id, qty) => {
      const account = get().account;
      if (!account || account.role !== "CLIENT") return;

      const nextCart = get().cart
        .map((item) => (item.id === id ? { ...item, quantity: roundQuantity(Math.max(0.1, qty), 3) } : item))
        .filter((item) => item.quantity > 0);

      await syncCart(nextCart, () => persistCartForAccount(nextCart));
    },
    removeFromCart: async (id) => {
      const account = get().account;
      if (!account || account.role !== "CLIENT") return;

      const nextCart = get().cart.filter((item) => item.id !== id);
      await syncCart(nextCart, () => persistCartForAccount(nextCart));
    },
    clearCart: async () => {
      const account = get().account;
      if (!account || account.role !== "CLIENT") {
        set({ cart: [] });
        return;
      }

      await syncCart([], async () => {
        await clearCartApi();
        return [];
      });
    },
    setClientStatus: async (clientId, status, clientType, priceTableId) => {
      const updated = await updateAdminCustomerStatus(clientId, status, clientType, priceTableId);
      const summary = toAdminClientSummaryFromDetail(updated);
      set((state) => ({
        clients: state.clients.map((client) => (client.id === clientId ? summary : client)),
      }));
    },
    loadDeliveryUnits: async () => {
      const account = get().account;
      if (!account || account.role !== "CLIENT") return;
      set({ deliveryUnitsStatus: "loading" });
      try {
        const units = await fetchMyDeliveryUnits();
        const current = get().selectedDeliveryUnitId;
        // Mantém a seleção anterior se ainda válida; auto-seleciona quando há exatamente UMA unidade.
        // Com várias e nenhuma escolhida, deixa null — a UI pede a escolha na primeira entrada.
        let selected = current && units.some((unit) => unit.id === current) ? current : null;
        if (!selected && units.length === 1) selected = units[0].id;
        set({ deliveryUnits: units, deliveryUnitsStatus: "ready", selectedDeliveryUnitId: selected });
        // Se a auto-seleção definiu a unidade, reprecifica o que já estiver carregado. Cobre também o
        // catálogo em "loading" (corrida da carga inicial): a hidratação COM unidade vence pela sequência.
        // O refetch do carrinho é INCONDICIONAL (não depende de cart.length): na corrida em que o carrinho
        // inicial chega vazio antes do auto-select, um cart.length===0 aqui deixaria o carrinho account-level
        // quando o GET inicial (sem unidade) resolvesse depois. O GET com a unidade fecha essa janela.
        if (selected && selected !== current) {
          if (get().catalogStatus !== "idle") await get().hydrateCatalog();
          await get().bootstrapSessionData();
        }
      } catch {
        set({ deliveryUnits: [], deliveryUnitsStatus: "ready" });
      }
    },
    setSelectedDeliveryUnit: async (unitId) => {
      if (get().selectedDeliveryUnitId === unitId) return;
      set({ selectedDeliveryUnitId: unitId });
      // O servidor é a autoridade de preço: reprecifica vitrine e carrinho pela nova unidade. Os itens do
      // carrinho NÃO são apagados — só reprecificados (o aviso ao cliente é responsabilidade da UI).
      if (get().catalogStatus !== "idle") await get().hydrateCatalog();
      if (get().cart.length > 0) await get().bootstrapSessionData();
    },
    hydrateCatalog: async () => {
      const hydrationId = ++lastCatalogHydrationId;
      set({ catalogStatus: "loading", catalogError: null });
      try {
        const snapshot = await fetchCatalogSnapshot(get().selectedDeliveryUnitId ?? undefined);
        // Descarta resposta obsoleta (outra hidratação mais recente já foi disparada, ex.: troca de unidade).
        if (hydrationId !== lastCatalogHydrationId) return;
        set({
          catalogCategories: snapshot.categories,
          catalogCutTypes: snapshot.cutTypes,
          catalogUnits: snapshot.units,
          products: snapshot.products,
          catalogStatus: "ready",
          catalogError: null,
        });
      } catch (error) {
        if (hydrationId !== lastCatalogHydrationId) return;
        set({
          catalogStatus: "error",
          catalogError: error instanceof Error ? error.message : "Nao foi possivel carregar o catalogo",
        });
      }
    },
    placeOrder: async ({ deliveryWindow, notes, deliveryUnitId, priceChangeAck }) => {
      const state = get();
      if (!state.account || state.account.role !== "CLIENT" || state.cart.length === 0) return null;

      const order = await checkoutOrder({ deliveryWindow, notes, deliveryUnitId, priceChangeAck });
      set({
        orders: [order, ...state.orders.filter((currentOrder) => currentOrder.id !== order.id)],
        cart: [],
      });
      return order;
    },
    updateOrder: async (orderId, input) => {
      const updated = await updateMyOrderApi(orderId, input);
      set((state) => ({ orders: mergeOrder(state.orders, updated) }));
      return updated;
    },
    deleteOrder: async (orderId) => {
      await deleteMyOrder(orderId);
      set((state) => ({ orders: state.orders.filter((order) => order.id !== orderId) }));
    },
    setOrderStatus: async (orderId, status) => {
      const updated = await updateOrderStatusApi(orderId, status);
      set((state) => ({ orders: mergeOrder(state.orders, updated) }));
    },
  };
});

